import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Loading } from '../components/Loading';
import { ShellLayout } from '../shell/ShellLayout';
import { HomePage } from '../features/public/HomePage';
import { ExplorePage } from '../features/public/ExplorePage';
import { MapPage } from '../features/public/MapPage';
import { PropertyPage } from '../features/public/PropertyPage';
import { PhotoSliderPage } from '../features/public/PhotoSliderPage';
import { NotFoundPage } from '../features/public/NotFoundPage';
import { BookingsPage } from '../features/client/BookingsPage';
import { TodayPage } from '../features/gestio/TodayPage';
import { MorePage } from '../features/gestio/MorePage';
import { StartRedirect } from '../features/auth/StartRedirect';

// Les pantalles que fan servir Firebase Auth o Firestore van en fragments a part (§3.3, §10.4).
const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const EmailLinkPage = lazy(() => import('../features/auth/EmailLinkPage'));
const AccountPage = lazy(() => import('../features/account/AccountPage'));
const UsersPage = lazy(() => import('../features/gestio/UsersPage'));
const PropertiesPage = lazy(() => import('../features/properties/PropertiesPage'));
const NewPropertyPage = lazy(() => import('../features/properties/NewPropertyPage'));
const EditPropertyPage = lazy(() => import('../features/properties/EditPropertyPage'));
const PricingPage = lazy(() => import('../features/pricing/PricingPage'));
const SpecsPage = lazy(() => import('../features/specs/SpecsPage'));
const MediaPage = lazy(() => import('../features/media/MediaPage'));
const MessagesPage = lazy(() => import('../features/inquiries/MessagesPage'));
const ConversationPage = lazy(() => import('../features/inquiries/ConversationPage'));
const OfferPage = lazy(() => import('../features/inquiries/OfferPage'));
const InquiryFormPage = lazy(() => import('../features/inquiries/InquiryFormPage'));
const PlanningPage = lazy(() => import('../features/calendar/PlanningPage'));
const PropertyCalendarPage = lazy(() => import('../features/calendar/PropertyCalendarPage'));
const SkinsPage = lazy(() => import('../features/skins/SkinsPage'));
const OpportunitiesPage = lazy(() => import('../features/opportunities/OpportunitiesPage'));
const OpportunityPage = lazy(() => import('../features/opportunities/OpportunityPage'));

/**
 * Mapa de pantalles (§10.4). La home va fora de la carcassa; la resta, dins.
 * Primer nivell = pestanyes; detall = fletxa enrere (i barra d'accions, si en té).
 */
export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/inici" element={<StartRedirect />} />
        {/* Slider de fotos (§10.2): immersiu, fora de la carcassa com la home. */}
        <Route path="/p/:slug/fotos" element={<PhotoSliderPage />} />

        <Route element={<ShellLayout />}>
          <Route path="/explora" element={<ExplorePage />} />
          <Route path="/mapa" element={<MapPage />} />
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/missatges" element={<MessagesPage />} />
          <Route path="/reserves" element={<BookingsPage />} />
          <Route path="/compte" element={<AccountPage />} />
          <Route path="/avui" element={<TodayPage />} />
          <Route path="/propietats" element={<PropertiesPage />} />
          <Route path="/calendari" element={<PlanningPage />} />
          <Route path="/mes" element={<MorePage />} />
          <Route path="/oportunitats" element={<OpportunitiesPage />} />
        </Route>

        <Route element={<ShellLayout detail />}>
          <Route path="/p/:slug" element={<PropertyPage />} />
          <Route path="/p/:slug/consulta" element={<InquiryFormPage />} />
          <Route path="/missatges/:id" element={<ConversationPage />} />
          <Route path="/missatges/:id/oferta" element={<OfferPage />} />
          <Route path="/entrar/enllac" element={<EmailLinkPage />} />
          <Route path="/mes/usuaris" element={<UsersPage />} />
          <Route path="/mes/skins" element={<SkinsPage />} />
          <Route path="/propietats/nova" element={<NewPropertyPage />} />
          <Route path="/propietats/:id" element={<EditPropertyPage />} />
          <Route path="/propietats/:id/tarifes" element={<PricingPage />} />
          <Route path="/propietats/:id/fitxa" element={<SpecsPage />} />
          <Route path="/propietats/:id/fotos" element={<MediaPage />} />
          <Route path="/propietats/:id/calendari" element={<PropertyCalendarPage />} />
          <Route path="/calendari/:id" element={<PropertyCalendarPage />} />
          <Route path="/oportunitats/:id" element={<OpportunityPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Adreça antiga de la gestió (abans de la carcassa). */}
        <Route path="/gestio/*" element={<Navigate to="/avui" replace />} />
      </Routes>
    </Suspense>
  );
}
